import {
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useMediaQuery } from "@/hooks/use-media-query";
import { local } from "@/lib/storage";
import { cn } from "@/lib/utils";
import { type OpenWiki, useOpenArtefact, useOpenWiki } from "../../router.tsx";
import { useChangeSet, useWikiPage } from "../../wiki/queries.ts";
import { WikiPanel } from "../../wiki/WikiPanel.tsx";
import { ArtefactPanel } from "./ArtefactPanel.tsx";
import { type ConversationScope, useArtefacts } from "./queries.ts";

const WIDTH_KEY = "esi:artefact-width";
const REM = 16;
/** The artefact column: 28rem to 40rem by default, widened up to half the screen (design §1). */
const MIN_WIDTH = 28 * REM;
const DEFAULT_WIDTH = 40 * REM;
const STEP = 2 * REM;

/**
 * The conversation and its right column (design §1). On wide screens (≥ 1024 px) the column holds
 * the knowledge panel, or the open artefact in its place: the knowledge panel stays mounted (only
 * hidden), so going back finds its state as it was; the user may hide it (`knowledge.shown`), the
 * thread then taking the room, mounted the same way. On narrow screens the artefact opens in a
 * full-height sheet. A standalone conversation (no `knowledge`) has the column only while an
 * artefact is open. The open artefact lives in the URL (`?artefact=&v=`).
 *
 * In a project conversation the column has a third occupant, the wiki side panel
 * (wiki-collaboration D8a, `?wiki=` or `?changes=`): one occupant at a time, an artefact taking
 * precedence; it closes to the knowledge panel's remembered state like an artefact does, and is
 * the same right sheet on narrow screens. Wiki links followed inside it keep a back stack.
 */
export function ArtefactLayout(props: {
  conversation: ReactNode;
  /** The conversation region's name (default "Conversation"). */
  conversationLabel?: string;
  /** The knowledge panel and its label; absent for standalone conversations. */
  knowledge?:
    | {
        panel: ReactNode;
        label: string;
        /** False while the user keeps it hidden (default shown). */
        shown?: boolean;
        /** The docked panel's ID, for the toggle's `aria-controls`. */
        id?: string;
      }
    | undefined;
  /** Null while the conversation does not exist yet ("new"). */
  scope: ConversationScope | null;
}) {
  const { conversation, knowledge, scope } = props;
  const { t } = useTranslation();
  const conversationLabel = props.conversationLabel ?? t("project.conversation");
  const wide = useMediaQuery("(min-width: 1024px)");
  const { open: requested, show, close: closeArtefact } = useOpenArtefact();
  const open = scope ? requested : null;
  const wikiState = useOpenWiki();
  // The wiki panel needs a project, and gives way to an artefact.
  const wiki = scope?.projectId && !open ? wikiState.open : null;
  const projectId = scope?.projectId ?? "";
  const [stack, setStack] = useState<OpenWiki[]>([]);
  const wikiKey = wiki ? JSON.stringify(wiki) : null;
  useEffect(() => {
    if (wikiKey === null) {
      setStack([]);
    }
  }, [wikiKey]);
  const close = open ? closeArtefact : wikiState.close;
  // The knowledge panel has the column when no artefact holds it, unless the user hides it.
  const docked = knowledge !== undefined && knowledge.shown !== false;
  const heading = useRef<HTMLHeadingElement | null>(null);
  const pendingFocus = useRef(false);
  const wikiHeading = useCallback((element: HTMLHeadingElement | null) => {
    heading.current = element;
    if (element && pendingFocus.current) {
      pendingFocus.current = false;
      element.focus();
    }
  }, []);
  const opener = useRef<HTMLElement | null>(null);
  const [width, setWidth] = useWidth();
  const artefactTitle = useArtefacts(
    scope ?? { projectId: null, conversationId: "" },
    open !== null,
  ).data?.artefacts.find((a) => a.id === open?.artefactId)?.title;
  // Shared with the panel's own queries: no extra request.
  const wikiPage = useWikiPage(
    projectId,
    wiki?.kind === "page" ? wiki.pageId : "",
    wiki?.kind === "page" ? (wiki.version ?? undefined) : undefined,
    wiki?.kind === "page",
  );
  const wikiChanges = useChangeSet(
    projectId,
    wiki?.kind === "changes" ? wiki.changeSetId : "",
    wiki?.kind === "changes",
  );
  const wikiTitle =
    wiki?.kind === "page"
      ? wikiPage.data?.title
      : wikiChanges.data && !wikiChanges.data.hiddenItems
        ? wikiChanges.data.title
        : undefined;
  const title = open ? artefactTitle : wikiTitle;

  // Focus moves to the panel's title when an artefact opens, and back to what opened it (the
  // card's "Open", a tree row) when it closes.
  const openId = open?.artefactId ?? wikiKey;
  useEffect(() => {
    if (openId === null) {
      pendingFocus.current = false;
      const target = opener.current;
      opener.current = null;
      if (target?.isConnected) {
        target.focus();
      }
      return;
    }
    if (!wide) {
      return;
    }
    const take = () => {
      // What opened this artefact (another may have been open already): focus returns there.
      const active = document.activeElement as HTMLElement | null;
      const panel = heading.current?.closest("section");
      if (active && active !== document.body && !panel?.contains(active)) {
        opener.current = active;
      }
      if (heading.current) {
        heading.current.focus();
      } else {
        // The wiki panel's title appears once its page or change set loads: focus it then.
        pendingFocus.current = true;
      }
    };
    take();
    // Opened from a menu item: the menu hands focus back to its trigger after this commit. If
    // focus lands on a menu trigger outside the panel just after opening, the title takes focus
    // again (once). Only a menu trigger: anything else is the user moving on (the composer),
    // and taking focus from it would lose what they type.
    const onFocusIn = (event: FocusEvent) => {
      const panel = heading.current?.closest("section");
      if (
        event.target instanceof HTMLElement &&
        !panel?.contains(event.target) &&
        event.target.matches("[aria-haspopup]")
      ) {
        stop();
        take();
      }
    };
    const stop = () => {
      document.removeEventListener("focusin", onFocusIn);
      window.clearTimeout(timer);
    };
    document.addEventListener("focusin", onFocusIn);
    const timer = window.setTimeout(stop, 500);
    return stop;
  }, [openId, wide]);

  // Stable: the panel's pin and skip effects depend on it.
  const artefactId = open?.artefactId ?? null;
  const onVersion = useCallback(
    (version: number) => {
      if (artefactId) {
        show({ artefactId, version }, { replace: true });
      }
    },
    [artefactId, show],
  );

  // In-panel navigation: a page followed from a link pushes the current one on the back stack;
  // choosing another item of the same change set does not.
  const navigateWiki = (next: OpenWiki) => {
    if (wiki && !(wiki.kind === "changes" && next.kind === "changes")) {
      setStack((current) => [...current, wiki]);
    }
    wikiState.show(next, { replace: true });
  };
  const backWiki =
    stack.length > 0
      ? () => {
          const previous = stack.at(-1) as OpenWiki;
          setStack((current) => current.slice(0, -1));
          wikiState.show(previous, { replace: true });
        }
      : undefined;
  const panel =
    wiki && scope?.projectId ? (
      <WikiPanel
        projectId={scope.projectId}
        open={wiki}
        headingRef={wikiHeading}
        onNavigate={navigateWiki}
        onBack={backWiki}
        // Back to the Knowledge panel as it was (D10): it stays mounted while the wiki holds the
        // column, so closing the wiki shows it again.
        onKnowledge={knowledge ? wikiState.close : undefined}
        onClose={wikiState.close}
        inSheet={!wide}
      />
    ) : open && scope ? (
      <ArtefactPanel
        scope={scope}
        open={open}
        headingRef={heading}
        onVersion={onVersion}
        onClose={closeArtefact}
        // Back to the panel only while it is shown: a hidden one is not there to go back to.
        onBack={docked ? closeArtefact : undefined}
        inSheet={!wide}
      />
    ) : null;

  const columns = !wide
    ? "grid-cols-[minmax(0,1fr)]"
    : panel
      ? "grid-cols-[minmax(0,1fr)_auto]"
      : docked
        ? "grid-cols-[minmax(0,1fr)_minmax(22rem,26rem)]"
        : "grid-cols-[minmax(0,1fr)]";

  return (
    <>
      <div className={cn("grid min-h-0 flex-1 grid-rows-[minmax(0,1fr)]", columns)}>
        <section aria-label={conversationLabel} className="flex min-h-0 min-w-0 flex-col">
          {conversation}
        </section>
        {wide && knowledge ? (
          <aside
            id={knowledge.id}
            aria-label={knowledge.label}
            hidden={panel !== null || !docked}
            className="flex min-h-0 flex-col border-l border-line bg-surface-rail"
          >
            {knowledge.panel}
          </aside>
        ) : null}
        {wide && panel ? (
          <section
            aria-label={
              wiki
                ? t("wiki.panel.label", { title: title ?? "" })
                : t("artefacts.panel.label", { title: title ?? t("artefacts.untitled") })
            }
            className="relative flex min-h-0 flex-col border-l border-line bg-surface"
            style={{ width: `min(${width}px, 50vw)`, minWidth: `${MIN_WIDTH}px` }}
            onKeyDown={(event) => {
              if (event.key === "Escape" && !event.defaultPrevented) {
                close();
              }
            }}
          >
            <Splitter width={width} onWidth={setWidth} />
            {panel}
          </section>
        ) : null}
      </div>
      {wide ? null : (
        <Sheet open={panel !== null} onOpenChange={(next) => (next ? undefined : close())}>
          <SheetContent
            side="right"
            className="w-full gap-0 p-0 sm:max-w-full"
            // The sheet itself takes focus (as the knowledge sheet does), not its first control.
            onOpenAutoFocus={(event) => {
              event.preventDefault();
              (event.currentTarget as HTMLElement).focus();
            }}
          >
            <SheetHeader className="border-b border-line pr-12">
              <SheetTitle className="truncate">
                {title ?? (wiki ? t("wiki.card.title") : t("artefacts.untitled"))}
              </SheetTitle>
              <SheetDescription className="sr-only">{conversationLabel}</SheetDescription>
            </SheetHeader>
            <div className="flex min-h-0 flex-1 flex-col">{panel}</div>
          </SheetContent>
        </Sheet>
      )}
    </>
  );
}

/** The column's width in pixels, remembered on this device (a convenience, never required). */
function useWidth(): [number, (next: number, persist?: boolean) => void] {
  const [width, setWidth] = useState(() => {
    const kept = Number(local.get(WIDTH_KEY));
    return Number.isFinite(kept) && kept >= MIN_WIDTH ? kept : DEFAULT_WIDTH;
  });
  return [
    width,
    (next, persist = true) => {
      const clamped = Math.round(Math.max(MIN_WIDTH, Math.min(next, maxWidth())));
      setWidth(clamped);
      if (persist) {
        local.set(WIDTH_KEY, String(clamped));
      }
    },
  ];
}

const maxWidth = () => Math.max(MIN_WIDTH, Math.floor(window.innerWidth / 2));

/**
 * The column's left edge: a vertical separator, operable with the arrow keys (Home and End for
 * the bounds) and by dragging.
 */
function Splitter(props: { width: number; onWidth: (width: number, persist?: boolean) => void }) {
  const { width, onWidth } = props;
  const { t } = useTranslation();
  const max = maxWidth();
  const shown = Math.min(width, max);
  const onKeyDown = (event: KeyboardEvent) => {
    const next =
      event.key === "ArrowLeft"
        ? shown + STEP
        : event.key === "ArrowRight"
          ? shown - STEP
          : event.key === "Home"
            ? MIN_WIDTH
            : event.key === "End"
              ? max
              : null;
    if (next !== null) {
      event.preventDefault();
      onWidth(next);
    }
  };
  return (
    // biome-ignore lint/a11y/useSemanticElements: a focusable window splitter is role="separator"
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={t("artefacts.panel.resize")}
      aria-valuemin={MIN_WIDTH}
      aria-valuemax={max}
      aria-valuenow={shown}
      tabIndex={0}
      onKeyDown={onKeyDown}
      onPointerDown={(event) => {
        event.preventDefault();
        const handle = event.currentTarget;
        handle.setPointerCapture?.(event.pointerId);
        const startX = event.clientX;
        const start = shown;
        let last = start;
        const move = (e: PointerEvent) => {
          last = start + (startX - e.clientX);
          onWidth(last, false);
        };
        // Released, cancelled or lost: the drag ends, and its width is remembered once.
        const end = () => {
          handle.removeEventListener("pointermove", move);
          handle.removeEventListener("pointerup", end);
          handle.removeEventListener("pointercancel", end);
          handle.removeEventListener("lostpointercapture", end);
          onWidth(last);
        };
        handle.addEventListener("pointermove", move);
        handle.addEventListener("pointerup", end);
        handle.addEventListener("pointercancel", end);
        handle.addEventListener("lostpointercapture", end);
      }}
      className="group absolute inset-y-0 -left-1.5 z-10 w-3 cursor-col-resize outline-none"
    >
      <span className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-transparent transition-colors duration-(--duration-fast) group-hover:bg-line-strong group-focus-visible:w-0.5 group-focus-visible:bg-focus" />
    </div>
  );
}
